import { Button, Label, Modal, Select, Tabs, Textarea, TextInput, ToggleSwitch } from "flowbite-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createProfile, updateProfile } from "../../api/profiles";
import { HiChatAlt2, HiOutlineInformationCircle, HiOutlineLightBulb, HiUsers } from "react-icons/hi";
import LogoUploader from "../uploaders/LogoUploader";

const CreateModal = ({ openModal, setOpenModal, fetchProfiles, profileToEdit, setProfileToEdit }) => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    jobDescription: '',
    mobileNumber: '',
    telephoneNumber: '',
    facebookUrl: '',
    linkedInUrl: '',
    twitterUrl: '',
    alias: '',
    photo: '',
    personalityStyle: '',
    communicationStyle: '',
    preferredGreetingStyle: '',
    responseStyle: '',
    useOfEmojis: false,
    useOfSlangInformalLanguage: false,
    levelOfPersonalization: '',
    preferredCallToActionStyle: '',
    responseSpeedPreference: '',
    levelOfFormalityInEmails: '',
    mentionClientsNameInInitialOutreach: false,
    useOfTemplatesPreApprovedResponses: false,
    customInstructionsForAi: '',
  });

  const personalityStyles = ['Friendly and Approachable', 'Professional and Polished', 'Humorous and Witty', 'Empathetic and Supportive', 'Assertive and Confident', 'Calm and Reassuring', 'Energetic and Enthusiastic'];
  const communicationStyles = ['Concise and Direct', 'Detailed and Informative', 'Conversational and Casual', 'Analytical and Insightful', 'Motivational and Encouraging', 'Neutral and Balanced'];
  const preferredGreetingStyles = ['Hi [Name], how can I help you today?', 'Hello [Name], what can I assist you with?', 'Good [morning/afternoon], [Name]. How may I assist?', 'Hey there! 😊 What can I do for you today?', 'Welcome back, [Name]! Ready to get started?'];
  const responseStyles = ['Bullet Points and Lists', 'Paragraph Format', 'Step-by-Step Instructions', 'Question and Answer Format', 'Visual/Emoji-Enhanced Responses', 'Mixed Style (Adpative)'];
  const levelsOfPersonalization = ['High – Personalised with name, context, history', 'Medium – Some contextual tailoring', 'Low – Generalised, non-personal', 'Adaptive – Adjusts based on conversation tone', 'None – Fully anonymous interactions'];
  const preferredCallToActionStyles = ['Direct ("Click here to proceed")', 'Suggestive ("You might want to try this…")', 'Friendly Reminder ("Don’t forget to…")', 'Motivational ("Let’s do this together!")', 'Passive ("Feel free to check this out…")', 'Step-by-Step Guidance ("Next, please follow this…")'];
  const responseSpeedPreferences = ['Instant (Fast replies with no delay)', 'Natural (Slight pause for realism)', 'Thoughtful (Slower, considered tone)', 'Adaptive (Speed based on context or urgency)'];
  const levelsOfFormalityInEmails = ['Very Formal (e.g., "Dear Sir/Madam…")', 'Formal (e.g., "Hello [Name], I hope this finds you well…")', 'Neutral (e.g., "Hi [Name], just following up…")', 'Informal (e.g., "Hey [Name], quick heads-up…")', 'Friendly Professional (e.g., "Hi [Name], great to hear from you…")'];

  useEffect(() => {
    if (profileToEdit) {
      setFormData({
        name: profileToEdit?.name,
        lastName: profileToEdit?.lastName,
        jobDescription: profileToEdit?.jobDescription,
        mobileNumber: profileToEdit?.mobileNumber,
        telephoneNumber: profileToEdit?.telephoneNumber,
        facebookUrl: profileToEdit?.facebookUrl,
        linkedInUrl: profileToEdit?.linkedInUrl,
        twitterUrl: profileToEdit?.twitterUrl,
        alias: profileToEdit?.alias,
        personalityStyle: profileToEdit?.personalityStyle,
        communicationStyle: profileToEdit?.communicationStyle,
        preferredGreetingStyle: profileToEdit?.preferredGreetingStyle,
        responseStyle: profileToEdit?.responseStyle,
        useOfEmojis: profileToEdit?.useOfEmojis,
        useOfSlangInformalLanguage: profileToEdit?.useOfSlangInformalLanguage,
        levelOfPersonalization: profileToEdit?.levelOfPersonalization,
        preferredCallToActionStyle: profileToEdit?.preferredCallToActionStyle,
        responseSpeedPreference: profileToEdit?.responseSpeedPreference,
        levelOfFormalityInEmails: profileToEdit?.levelOfPersonalization,
        mentionClientsNameInInitialOutreach: profileToEdit?.mentionClientsNameInInitialOutreach,
        useOfTemplatesPreApprovedResponses: profileToEdit?.useOfTemplatesPreApprovedResponses,
        customInstructionsForAi: profileToEdit?.customInstructionsForAi,
      });
    } else {
      setFormData({
        name: '',
        lastName: '',
        jobDescription: '',
        mobileNumber: '',
        telephoneNumber: '',
        facebookUrl: '',
        linkedInUrl: '',
        twitterUrl: '',
        alias: '',
        personalityStyle: '',
        communicationStyle: '',
        preferredGreetingStyle: '',
        responseStyle: '',
        useOfEmojis: false,
        useOfSlangInformalLanguage: false,
        levelOfPersonalization: '',
        preferredCallToActionStyle: '',
        responseSpeedPreference: '',
        levelOfFormalityInEmails: '',
        mentionClientsNameInInitialOutreach: false,
        useOfTemplatesPreApprovedResponses: false,
        customInstructionsForAi: '',
      });
    }
  }, [profileToEdit]);

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const data = new FormData();

      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'photo') {
          const fileInput = document.querySelector('input[name="photo"]');

          if (fileInput && fileInput.files.length) {
            data.append('photo', fileInput.files[0]);
          }
        } else if (Array.isArray(value)) {
          value.forEach(item => data.append(`${key}[]`, item));
        } else {
          data.append(key, value);
        }
      });

      if (profileToEdit) {
        await updateProfile({
          id: profileToEdit._id,
          data,
        });

        toast.success(`${formData.name} profile has been updated successfully`);
      } else {
        await createProfile(data);

        toast.success(`${formData.name} profile has been created successfully`);
      }

      fetchProfiles();
      setOpenModal(false);
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || error.message || 'Failed to process request');
    } finally {
      setLoading(false);
    }
  }

  const handleClose = () => {
    setOpenModal(false);
    setProfileToEdit(false);
  }

  return (
    <Modal show={openModal} size="3xl" onClose={handleClose} popup>
      <Modal.Header />
      <Modal.Body>
        <form onSubmit={handleSubmit} className="space-y-6">
          <h3 className="text-xl font-medium text-gray-900">{profileToEdit ? 'Edit' : 'Add'} profile</h3>

          <Tabs variant="default">
            <Tabs.Item active title="Basic Information" icon={HiOutlineInformationCircle}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="name" value="First Name" />
                  </div>
                  <TextInput
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="lastName" value="Last Name" />
                  </div>
                  <TextInput
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="alias" value="Email Alias" />
                  </div>
                  <TextInput
                    id="alias"
                    name="alias"
                    value={formData.alias}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="jobDescription" value="Job Description" />
                  </div>
                  <TextInput
                    id="jobDescription"
                    name="jobDescription"
                    value={formData.jobDescription}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="mobileNumber" value="Mobile Number" />
                  </div>
                  <TextInput
                    id="mobileNumber"
                    name="mobileNumber"
                    value={formData.mobileNumber}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="telephoneNumber" value="Telephone Number" />
                  </div>
                  <TextInput
                    id="telephoneNumber"
                    name="telephoneNumber"
                    value={formData.telephoneNumber}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="photo" value="Photo" />
                  </div>
                  <LogoUploader
                    name="photo"
                    value={formData.photo || ''}
                    onChange={handleChange}
                    existingLogo={profileToEdit?.photo}
                  />
                </div>
              </div>
            </Tabs.Item>
            <Tabs.Item active title="AI Communication & Personality" icon={HiChatAlt2}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="personalityStyle" value="Personality Style" />
                  </div>
                  <Select
                    id="personalityStyle"
                    name="personalityStyle"
                    value={formData.personalityStyle}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {personalityStyles.map(personalityStyle => (
                      <option key={personalityStyle} value={personalityStyle}>
                        {personalityStyle}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="communicationStyle" value="Communication Style" />
                  </div>
                  <Select
                    id="communicationStyle"
                    name="communicationStyle"
                    value={formData.communicationStyle}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {communicationStyles.map(communicationStyle => (
                      <option key={communicationStyle} value={communicationStyle}>
                        {communicationStyle}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="preferredGreetingStyle" value="Preferred Greeting Style" />
                  </div>
                  <Select
                    id="preferredGreetingStyle"
                    name="preferredGreetingStyle"
                    value={formData.preferredGreetingStyle}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {preferredGreetingStyles.map(preferredGreetingStyle => (
                      <option key={preferredGreetingStyle} value={preferredGreetingStyle}>
                        {preferredGreetingStyle}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="responseStyle" value="Response Style" />
                  </div>
                  <Select
                    id="responseStyle"
                    name="responseStyle"
                    value={formData.responseStyle}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {responseStyles.map(responseStyle => (
                      <option key={responseStyle} value={responseStyle}>
                        {responseStyle}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <ToggleSwitch
                    checked={formData.useOfEmojis}
                    label="Use of Emojis?"
                    onChange={() => {
                      handleChange({
                        target: {
                          name: 'useOfEmojis',
                          value: !formData.useOfEmojis,
                        }
                      });
                    }}
                    theme={{
                      toggle: {
                        base: 'after:rounded-full rounded-full border group-focus:ring-4 group-focus:ring-cyan-500/25',
                        checked: {
                          on: 'after:bg-white after:translate-x-full',
                          off: 'after:bg-gray-400 dark:after:bg-gray-500 border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700',
                        },
                      },
                    }}
                  />
                </div>

                <div>
                  <ToggleSwitch
                    checked={formData.useOfSlangInformalLanguage}
                    label="Use of Slang & Informal Language?"
                    onChange={() => {
                      handleChange({
                        target: {
                          name: 'useOfSlangInformalLanguage',
                          value: !formData.useOfSlangInformalLanguage,
                        }
                      });
                    }}
                    theme={{
                      toggle: {
                        base: 'after:rounded-full rounded-full border group-focus:ring-4 group-focus:ring-cyan-500/25',
                        checked: {
                          on: 'after:bg-white after:translate-x-full',
                          off: 'after:bg-gray-400 dark:after:bg-gray-500 border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700',
                        },
                      },
                    }}
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="levelOfPersonalization" value="Level of Personalization" />
                  </div>
                  <Select
                    id="levelOfPersonalization"
                    name="levelOfPersonalization"
                    value={formData.levelOfPersonalization}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {levelsOfPersonalization.map(levelOfPersonalization => (
                      <option key={levelOfPersonalization} value={levelOfPersonalization}>
                        {levelOfPersonalization}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="preferredCallToActionStyle" value="Preferred Call-to-Action Style" />
                  </div>
                  <Select
                    id="preferredCallToActionStyle"
                    name="preferredCallToActionStyle"
                    value={formData.preferredCallToActionStyle}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {preferredCallToActionStyles.map(preferredCallToActionStyle => (
                      <option key={preferredCallToActionStyle} value={preferredCallToActionStyle}>
                        {preferredCallToActionStyle}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="responseSpeedPreference" value="Response Speed Preference" />
                  </div>
                  <Select
                    id="responseSpeedPreference"
                    name="responseSpeedPreference"
                    value={formData.responseSpeedPreference}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {responseSpeedPreferences.map(responseSpeedPreference => (
                      <option key={responseSpeedPreference} value={responseSpeedPreference}>
                        {responseSpeedPreference}
                      </option>
                    ))}
                  </Select>
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="levelOfFormalityInEmails" value="Level of Formality in Emails" />
                  </div>
                  <Select
                    id="levelOfFormalityInEmails"
                    name="levelOfFormalityInEmails"
                    value={formData.levelOfFormalityInEmails}
                    onChange={handleChange}
                  >
                    <option
                      value=""
                      disabled
                    >
                      Select
                    </option>
                    {levelsOfFormalityInEmails.map(levelOfFormalityInEmails => (
                      <option key={levelOfFormalityInEmails} value={levelOfFormalityInEmails}>
                        {levelOfFormalityInEmails}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
            </Tabs.Item>
            <Tabs.Item active title="Social Presence & Engagement" icon={HiUsers}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="facebookUrl" value="Facebook URL" />
                  </div>
                  <TextInput
                    id="facebookUrl"
                    type="url"
                    name="facebookUrl"
                    value={formData.facebookUrl}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="linkedInUrl" value="LinkedIn URL" />
                  </div>
                  <TextInput
                    id="linkedInUrl"
                    type="url"
                    name="linkedInUrl"
                    value={formData.linkedInUrl}
                    onChange={handleChange}
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="twitterUrl" value="Twitter URL" />
                  </div>
                  <TextInput
                    id="twitterUrl"
                    type="url"
                    name="twitterUrl"
                    value={formData.twitterUrl}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </Tabs.Item>
            <Tabs.Item active title="AI Usage & Rules" icon={HiOutlineLightBulb}>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <ToggleSwitch
                    checked={formData.mentionClientsNameInInitialOutreach}
                    label="Mention Client’s Name in Initial Outreach?"
                    onChange={() => {
                      handleChange({
                        target: {
                          name: 'mentionClientsNameInInitialOutreach',
                          value: !formData.mentionClientsNameInInitialOutreach,
                        }
                      });
                    }}
                    theme={{
                      toggle: {
                        base: 'after:rounded-full rounded-full border group-focus:ring-4 group-focus:ring-cyan-500/25',
                        checked: {
                          on: 'after:bg-white after:translate-x-full',
                          off: 'after:bg-gray-400 dark:after:bg-gray-500 border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700',
                        },
                      },
                    }}
                  />
                </div>

                <div>
                  <ToggleSwitch
                    checked={formData.useOfTemplatesPreApprovedResponses}
                    label="Use of Templates & Pre-approved Responses?"
                    onChange={() => {
                      handleChange({
                        target: {
                          name: 'useOfTemplatesPreApprovedResponses',
                          value: !formData.useOfTemplatesPreApprovedResponses,
                        }
                      });
                    }}
                    theme={{
                      toggle: {
                        base: 'after:rounded-full rounded-full border group-focus:ring-4 group-focus:ring-cyan-500/25',
                        checked: {
                          on: 'after:bg-white after:translate-x-full',
                          off: 'after:bg-gray-400 dark:after:bg-gray-500 border-gray-200 bg-gray-200 dark:border-gray-600 dark:bg-gray-700',
                        },
                      },
                    }}
                  />
                </div>

                <div>
                  <div className="mb-2 block">
                    <Label htmlFor="customInstructionsForAi" value="Custom Instructions for AI" />
                  </div>
                  <Textarea
                    id="customInstructionsForAi"
                    name="customInstructionsForAi"
                    value={formData.customInstructionsForAi}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>
              </div>
            </Tabs.Item>
          </Tabs>

          <div className="w-full">
            <Button
              type="submit"
              isProcessing={loading}
              disabled={loading}
            >
              {profileToEdit ? 'Update' : 'Add'}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  )
}

export default CreateModal;
